import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { centralService } from '../../../services/centralService';
import Input from '../../ui/Input';
import Select from '../../ui/Select';
import Button from '../../ui/Button';
import { 
  INSTALLMENT_RELEASE_TYPES,
  INSTALLMENT_NUMBER_OPTIONS,
  DEFAULT_INSTALLMENT_DATA
} from '../../../constants/centralConstants';
import { validateInstallmentForm } from '../../../utils/centralValidation';
import { financialYearUtils } from '../../../utils/helpers';

const StateInstallmentForm = () => {
  const { user } = useAuth();
  const { isMobile, getResponsiveValue } = useTheme();
  
  const [formData, setFormData] = useState({
    ...DEFAULT_INSTALLMENT_DATA,
    releaseType: INSTALLMENT_RELEASE_TYPES.STATE
  });
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [projectOptions, setProjectOptions] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);

  // Responsive configurations
  const titleSize = getResponsiveValue({
    mobile: 'text-xl',
    desktop: 'text-2xl'
  });

  const gridCols2 = getResponsiveValue({
    mobile: 'grid-cols-1',
    desktop: 'grid-cols-2'
  });

  const gridCols3 = getResponsiveValue({
    mobile: 'grid-cols-1',
    tablet: 'grid-cols-2',
    desktop: 'grid-cols-3'
  });

  const gridGap = getResponsiveValue({
    mobile: 'gap-4',
    desktop: 'gap-6'
  });

  // Load all central projects (both DoNER and NEC) for dropdown
  useEffect(() => {
    loadAllCentralProjects();
  }, []);

  // Set initial financial year
  useEffect(() => {
    const currentFY = financialYearUtils.getCurrentFinancialYear();
    setFormData(prev => ({
      ...prev,
      financialYear: currentFY
    }));
  }, []);

  const loadAllCentralProjects = async () => {
    try {
      setLoadingProjects(true);
      const result = await centralService.getCentralProjectsForStateInstallments();
      
      if (result.success) {
        setProjectOptions(result.data);
      }
    } catch (error) {
      console.error('Error loading central projects:', error);
    } finally {
      setLoadingProjects(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear errors when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
    
    if (success) setSuccess(false);
  };

  const handleProjectSelect = (projectId) => {
    const selectedProject = projectOptions.find(p => p.value === projectId);
    
    setFormData(prev => ({
      ...prev,
      projectId: projectId,
      projectName: selectedProject ? selectedProject.label : '',
      projectType: selectedProject ? selectedProject.type : ''
    }));
    
    // Clear project error
    if (errors.projectId) {
      setErrors(prev => ({
        ...prev,
        projectId: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validation = validateInstallmentForm(formData);
    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }
    
    try {
      setLoading(true);
      setErrors({});
      
      const result = await centralService.submitInstallment(formData, user.uid);
      
      if (result.success) {
        setSuccess(true);
        setSubmitMessage('State installment submitted successfully!');
        
        // Reset form
        setFormData({
          ...DEFAULT_INSTALLMENT_DATA,
          releaseType: INSTALLMENT_RELEASE_TYPES.STATE,
          financialYear: financialYearUtils.getCurrentFinancialYear()
        });
        
        setTimeout(() => {
          setSuccess(false);
          setSubmitMessage('');
        }, 5000);
      }
    } catch (error) {
      console.error('Error submitting state installment:', error);
      setErrors({ general: error.message });
    } finally {
      setLoading(false);
    }
  };

  const financialYearOptions = financialYearUtils.getFinancialYearOptions();

  return (
    <div className="space-y-4">
      {/* Success Message */}
      {success && (
        <div className="bg-success-subtle border border-border-primary rounded-xl p-3">
          <div className="flex items-center">
            <svg className="w-4 h-4 text-success mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span className="text-success font-medium text-sm">{submitMessage}</span>
          </div>
        </div>
      )}

      {/* Header */}
      <div>
        <h1 className={`${titleSize} font-bold text-text-primary mb-1`}>State Installments</h1>
        <p className="text-text-secondary text-sm">
          Submit state installment details for both DoNER and NEC projects
        </p>
      </div>

      {/* Form */}
      <div>
        <div>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* General Error */}
            {errors.general && (
              <div className="bg-error-subtle border border-border-error rounded-xl p-3">
                <div className="flex items-center">
                  <svg className="w-4 h-4 text-error mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <span className="text-error text-sm">{errors.general}</span>
                </div>
              </div>
            )}

            {/* Basic Information - Two columns */}
            <div className={`grid ${gridCols2} ${gridGap}`}>
              <div>
                <Select
                  label="Financial Year"
                  value={formData.financialYear}
                  onChange={(e) => handleInputChange('financialYear', e.target.value)}
                  options={financialYearOptions}
                  error={errors.financialYear}
                  required
                />
              </div>

              <div>
                <Select
                  label="Name of Project"
                  value={formData.projectId}
                  onChange={(e) => handleProjectSelect(e.target.value)}
                  options={projectOptions}
                  error={errors.projectId}
                  required
                  placeholder={loadingProjects ? "Loading projects..." : "Select Project (DoNER/NEC)"}
                  disabled={loadingProjects}
                />
              </div>
            </div>

            {/* Release Information - Two columns (removed Amount Released By) */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-text-primary border-b border-border-primary pb-2">
                Release Information
              </h3>
              
              <div className={`grid ${gridCols2} ${gridGap}`}>
                <Select
                  label="Installment Number"
                  value={formData.installmentNumber}
                  onChange={(e) => handleInputChange('installmentNumber', e.target.value)}
                  options={INSTALLMENT_NUMBER_OPTIONS}
                  error={errors.installmentNumber}
                  required
                  placeholder="Select Installment"
                />

                <Input
                  label="Release Date"
                  type="date"
                  value={formData.releaseDate}
                  onChange={(e) => handleInputChange('releaseDate', e.target.value)}
                  error={errors.releaseDate}
                  required
                />
              </div>
            </div>

            {/* Financial Information & Physical Progress - Two columns */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-text-primary border-b border-border-primary pb-2">
                Financial & Progress Details
              </h3>
              
              <div className={`grid ${gridCols2} ${gridGap}`}>
                <Input
                  label="Amount (in lakh)"
                  type="number"
                  value={formData.amount}
                  onChange={(e) => handleInputChange('amount', e.target.value)}
                  placeholder="Enter amount in lakhs"
                  error={errors.amount}
                  required
                  icon={
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                    </svg>
                  }
                />

                <Input
                  label="Physical Progress (%)"
                  type="number"
                  step="0.01"
                  value={formData.physicalProgress}
                  onChange={(e) => handleInputChange('physicalProgress', e.target.value)}
                  placeholder="Enter progress percentage (0-100)"
                  error={errors.physicalProgress}
                  required
                  min="0"
                  max="100"
                  icon={
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  }
                />
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end pt-4 border-t border-border-primary">
              <Button
                type="submit"
                loading={loading}
                disabled={loading || loadingProjects}
                size="md"
                className={isMobile ? 'w-full' : ''}
              >
                {loading ? 'Submitting...' : 'Submit State Installment'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default StateInstallmentForm;